/**
 * Token utility functions for extracting non-PII identifiers from Azure AD tokens
 * 
 * Security: Only extracts oid (Object ID) and tid (Tenant ID) - no PII like email, name, upn
 */

import { safeBase64UrlDecode, safeJsonParse, isValidJwtFormat } from './securityUtils';

/**
 * Decodes a JWT token without verification (for extracting claims only)
 * Supports both:
 * - Standard JWT (3 parts: header.payload.signature) - Azure AD tokens
 * - Custom tokens (2 parts: header.payload) - Our custom OID tokens
 * 
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded token payload or null if invalid
 */
export function decodeJwt(token) {
    if (!isValidJwtFormat(token)) {
        return null;
    }

    try {
        const parts = token.split('.');
        
        // Support both standard JWT (3 parts) and custom tokens (2 parts)
        if (parts.length !== 3 && parts.length !== 2) {
            console.warn('Token has invalid number of parts:', parts.length);
            return null;
        }

        // Decode base64url encoded payload (second part) using secure method
        const payload = parts[1];
        const decodedPayload = safeBase64UrlDecode(payload);
        
        if (!decodedPayload) {
            return null;
        }
        
        return safeJsonParse(decodedPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
}

/**
 * Extracts oid (Object ID) and tid (Tenant ID) from an ID token
 * These are non-PII identifiers that can be safely stored
 * 
 * @param {string} idToken - Azure AD ID token
 * @returns {{oid: string|null, tid: string|null}} Object containing oid and tid
 */
export function extractNonPiiFromIdToken(idToken) {
    if (!idToken) {
        return { oid: null, tid: null };
    }

    const decoded = decodeJwt(idToken);
    if (!decoded) {
        return { oid: null, tid: null };
    }

    return {
        oid: decoded.oid || null,
        tid: decoded.tid || null
    };
}

/**
 * Decodes and extracts claims from an access token
 * @param {string} accessToken - JWT access token
 * @returns {object|null} Decoded token claims or null if invalid
 */
export function decodeAccessToken(accessToken) {
    return decodeJwt(accessToken);
}

/**
 * Generates a unique session ID for each token
 * Uses crypto.getRandomValues for cryptographically secure randomness
 * 
 * @returns {string} 32-character hex string (16 bytes = 128 bits)
 */
function generateSessionId() {
    try {
        // Use crypto API for secure random number generation
        const array = new Uint8Array(16); // 16 bytes = 128 bits
        crypto.getRandomValues(array);
        
        // Convert to hex string
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    } catch (error) {
        // Fallback if crypto API not available (shouldn't happen in modern browsers)
        console.warn('Crypto API not available, falling back to Math.random');
        return Date.now().toString(36) + Math.random().toString(36).substring(2);
    }
}

/**
 * Creates a custom token containing only oid (non-PII identifier)
 * This token is sent to backend instead of the original JWT token (which contains PII)
 * 
 * Format: base64(header).base64(payload)
 * Header: { "typ": "JWT", "alg": "none" }
 * Payload: { "oid": "<user-object-id>", "sid": "<session-id>", "iat": <timestamp>, "exp": <expiry>, "isAppFlow": <boolean>, "ati": "<azure-token-id>" }
 * 
 * When decoded, only oid, sid, isAppFlow, and ati are visible in the payload.
 * Note: azp and aud are used for token generation context but not included in the token payload.
 * 
 * @param {object} options - Token creation options
 * @param {string} options.oid - User object ID (non-PII identifier) - ONLY visible claim when decoded
 * @param {number} [options.azureTokenExp] - Azure token expiry timestamp (Unix seconds). If provided, custom token expires at same time.
 * @param {string} [options.azureTokenId] - Azure token unique ID (jti or uti claim) for tracking/correlation
 * @param {boolean} [options.isAppFlow=false] - Whether this is an app flow (client_credentials) vs user flow
 * @param {string} [options.clientId=null] - Client ID for app flow (required when isAppFlow is true)
 * @returns {string} Custom token string
 */
export function createCustomOidToken(options) {
    // Support legacy signature: createCustomOidToken(oid, azp, aud, expiryMinutes, isAppFlow, clientId)
    let oid, azureTokenExp, azureTokenId, isAppFlow, clientId;
    
    if (typeof options === 'string') {
        // Legacy call: createCustomOidToken(oid, azp, aud, expiryMinutes, isAppFlow, clientId)
        oid = options;
        const expiryMinutes = arguments[3] || 60;
        isAppFlow = arguments[4] || false;
        clientId = arguments[5] || null;
        azureTokenExp = null;
        azureTokenId = null;
        
        const now = Math.floor(Date.now() / 1000);
        azureTokenExp = now + (expiryMinutes * 60);
    } else {
        // New call: createCustomOidToken({ oid, azureTokenExp, azureTokenId, isAppFlow, clientId })
        oid = options.oid;
        azureTokenExp = options.azureTokenExp;
        azureTokenId = options.azureTokenId;
        isAppFlow = options.isAppFlow || false;
        clientId = options.clientId || null;
    }

    if (!oid || typeof oid !== 'string') {
        throw new Error('OID is required to create custom token');
    }

    const now = Math.floor(Date.now() / 1000);
    
    // Use Azure token expiry if provided, otherwise default to 60 minutes
    const exp = azureTokenExp || (now + (60 * 60));
    
    const sessionId = generateSessionId(); // UNIQUE identifier for this token

    // Create header (simple JSON, no signing needed - backend just extracts oid)
    const header = {
        typ: 'JWT',
        alg: 'none'
    };

    // Create payload with oid, sid, and isAppFlow flag
    const payload = {
        oid: oid,         // Object ID (user OID for user flow, service principal OID for app flow)
        sid: sessionId,   // Session ID - makes each token unique (for multi-device logout)
        iat: now,
        exp: exp,
        isAppFlow: isAppFlow  // Flag to indicate app flow (skip Graph API user lookup)
    };
    
    // Include Azure token unique ID for tracking/correlation (if available)
    // This helps backend correlate custom token with original Azure token
    if (azureTokenId) {
        payload.ati = azureTokenId; // Azure Token ID (jti or uti from Azure token)
    }
    
    // For app flow, include clientId so backend can look up user in MongoDB
    if (isAppFlow && clientId) {
        payload.clientId = clientId;
    }

    // Encode as base64url (JWT format)
    const base64UrlEncode = (str) => {
        return btoa(JSON.stringify(str))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    };

    const encodedHeader = base64UrlEncode(header);
    const encodedPayload = base64UrlEncode(payload);

    // Return token in JWT format (header.payload) - no signature since we're not signing
    return `${encodedHeader}.${encodedPayload}`;
}

/**
 * Extracts Azure token unique ID (jti or uti) from decoded token
 * Azure AD tokens use 'uti' (unique token identifier) claim
 * Some tokens may use 'jti' (JWT ID) claim
 * 
 * @param {object} decodedToken - Decoded JWT payload
 * @returns {string|null} Azure token unique ID or null if not found
 */
export function getAzureTokenId(decodedToken) {
    if (!decodedToken) return null;
    // Azure AD uses 'uti' claim for unique token identifier
    // Fall back to 'jti' if uti is not present
    return decodedToken.uti || decodedToken.jti || null;
}


